import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title
    document.title = 'About Revivacorp - Boca Raton Business Turnaround Experts';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn about Revivacorp, a trusted business turnaround and restructuring consulting firm in Boca Raton. Our mission is to transform struggling corporations into thriving enterprises through strategic guidance.');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/about`);
    
    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'About Revivacorp - Boca Raton Business Turnaround Experts' },
      { property: 'og:description', content: 'Learn about Revivacorp, a trusted business turnaround and restructuring consulting firm in Boca Raton. Our mission is to transform struggling corporations into thriving enterprises through strategic guidance.' },
      { property: 'og:url', content: `${siteUrl}/about` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: `${siteUrl}/og-image.jpg` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    // Set Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'About Revivacorp - Boca Raton Business Turnaround Experts' },
      { name: 'twitter:description', content: 'Learn about Revivacorp, a trusted business turnaround and restructuring consulting firm in Boca Raton. Our mission is to transform struggling corporations into thriving enterprises through strategic guidance.' },
      { name: 'twitter:image', content: `${siteUrl}/og-image.jpg` }
    ];
    
    twitterTags.forEach(tag => {
      let element = document.querySelector(`meta[name="${tag.name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', tag.name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    const aboutPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Revivacorp',
      url: `${siteUrl}/about`,
      description: 'Transforming struggling corporations into thriving enterprises through strategic guidance and proven turnaround methodologies',
      mainEntity: {
        '@type': 'Organization',
        name: 'Revivacorp',
        description: 'Business turnaround and restructuring consulting firm',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2255 Glades Rd',
          addressLocality: 'Boca Raton',
          addressRegion: 'FL',
          postalCode: '33431',
          addressCountry: 'US'
        }
      }
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'About',
          item: `${siteUrl}/about`
        }
      ]
    };

    const aboutScript = document.createElement('script');
    aboutScript.type = 'application/ld+json';
    aboutScript.text = JSON.stringify(aboutPageSchema);
    document.head.appendChild(aboutScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(aboutScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center">
        <img
          src="https://public.readdy.ai/ai/img_res/ba2e63f253e06d06918b4fee3dfcb084.jpg"
          alt="About Revivacorp"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">About Revivacorp</h1>
          <p className="text-xl text-white/95 leading-relaxed">
            Transforming struggling corporations into thriving enterprises through strategic guidance and proven turnaround methodologies
          </p>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-2xl shadow-sm">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Mission</h2>
              <p className="text-slate-700 leading-relaxed text-base">
                To provide comprehensive turnaround and restructuring services that help corporations overcome financial challenges, optimize operations, and achieve sustainable growth. We are committed to delivering practical solutions that create lasting value for our clients and their stakeholders.
              </p>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-white p-10 rounded-2xl shadow-sm">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Vision</h2>
              <p className="text-slate-700 leading-relaxed text-base">
                To be the most trusted partner for corporations facing financial distress, recognized for our expertise in business turnaround, strategic restructuring, and operational excellence. We envision a future where every struggling business has access to the guidance needed to thrive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Story</h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20business%20consulting%20team%20analyzing%20financial%20charts%20and%20strategic%20planning%20documents%20in%20modern%20corporate%20boardroom%20with%20collaborative%20atmosphere%20and%20executive%20leadership%20presence&width=600&height=400&seq=about-story-img&orientation=landscape"
                alt="Our Story"
                className="w-full h-[400px] object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <p className="text-slate-700 leading-relaxed text-base mb-6">
                Founded with a singular purpose—to help corporations navigate their most challenging times—Revivacorp has grown into a trusted partner for businesses facing financial distress and operational difficulties.
              </p>
              <p className="text-slate-700 leading-relaxed text-base mb-6">
                Our team brings decades of combined experience in corporate turnaround, restructuring, and strategic management. We've worked with companies across diverse industries, from manufacturing to technology, helping them overcome obstacles and emerge stronger.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                What sets us apart is our hands-on approach. We don't just provide recommendations—we work alongside your team to implement solutions, monitor progress, and ensure sustainable results. Every engagement is tailored to your unique situation, challenges, and goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-slate-600 text-base max-w-2xl mx-auto">
              These principles guide everything we do and shape how we serve our clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-shield-check-line', title: 'Integrity', description: 'We operate with the highest ethical standards, providing honest assessments and transparent communication at every stage.' },
              { icon: 'ri-lightbulb-line', title: 'Innovation', description: 'We bring creative solutions to complex problems, leveraging cutting-edge strategies and proven methodologies.' },
              { icon: 'ri-team-line', title: 'Collaboration', description: 'We work as an extension of your team, fostering partnerships built on trust, respect, and shared goals.' },
              { icon: 'ri-trophy-line', title: 'Excellence', description: 'We are committed to delivering exceptional results that exceed expectations and create lasting value.' }
            ].map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Revivacorp</h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-bar-chart-box-line', title: 'Proven Track Record', description: 'Successfully guided over 200 companies through turnaround and restructuring processes with measurable results.' },
              { icon: 'ri-user-star-line', title: 'Expert Team', description: 'Our consultants bring deep industry knowledge and hands-on experience in corporate turnaround and crisis management.' },
              { icon: 'ri-settings-3-line', title: 'Customized Solutions', description: 'Every engagement is tailored to your specific challenges, industry dynamics, and organizational culture.' },
              { icon: 'ri-time-line', title: 'Rapid Response', description: 'We understand urgency in crisis situations and mobilize quickly to assess, plan, and implement solutions.' },
              { icon: 'ri-line-chart-line', title: 'Sustainable Results', description: 'We focus on long-term viability, not just short-term fixes, ensuring your business thrives beyond our engagement.' },
              { icon: 'ri-hand-heart-line', title: 'Client-Centric Approach', description: 'Your success is our priority. We maintain open communication and work collaboratively throughout the process.' }
            ].map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-teal-600 to-teal-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl text-white/95 mb-8 leading-relaxed">
            Let's discuss how we can help your corporation overcome challenges and achieve sustainable growth
          </p>
          <a href="/contact">
            <button className="px-8 py-4 bg-white text-teal-600 text-base font-semibold rounded-full hover:bg-slate-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
              Schedule a Consultation
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}